/**
 * 네이버 맵 API 동적 로드 유틸리티
 */

let isScriptLoaded = false
let isScriptLoading = false
let loadPromise: Promise<void> | null = null
const NAVER_MAP_SCRIPT_ID = 'naver-map-script'
const NAVER_MARKER_CLUSTER_SCRIPT_ID = 'naver-marker-cluster-script'
const NAVER_MARKER_CLUSTER_SCRIPT_URL =
  'https://navermaps.github.io/maps.js.ncp/docs/js/MarkerClustering.js'

const ensureScriptLoaded = (
  scriptId: string,
  scriptUrl: string
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(
      scriptId
    ) as HTMLScriptElement | null
    if (existingScript) {
      const isReady = existingScript.getAttribute('data-loaded') === 'true'
      if (isReady) {
        resolve()
        return
      }

      existingScript.addEventListener('load', () => resolve(), { once: true })
      existingScript.addEventListener(
        'error',
        () => reject(new Error(`스크립트 로드 실패: ${scriptUrl}`)),
        { once: true }
      )
      return
    }

    const script = document.createElement('script')
    script.id = scriptId
    script.type = 'text/javascript'
    script.src = scriptUrl
    script.async = true

    script.onload = () => {
      script.setAttribute('data-loaded', 'true')
      resolve()
    }
    script.onerror = () => {
      reject(new Error(`스크립트 로드 실패: ${scriptUrl}`))
    }

    document.head.appendChild(script)
  })
}

/**
 * 네이버 맵 API 스크립트를 동적으로 로드
 */
export const loadNaverMapScript = (): Promise<void> => {
  console.log('[NaverMapLoader] loadNaverMapScript 호출', {
    isScriptLoaded,
    isScriptLoading,
    hasNaver: typeof (window as any).naver,
    hasMaps: typeof (window as any).naver?.maps,
  })

  if (isScriptLoaded) {
    console.log('[NaverMapLoader] 이미 로드됨, resolve')
    return Promise.resolve()
  }

  if (isScriptLoading && loadPromise) {
    console.log('[NaverMapLoader] 로딩 중, 기존 promise 반환')
    return loadPromise
  }

  isScriptLoading = true
  loadPromise = new Promise((resolve, reject) => {
    const clientId = process.env.REACT_APP_NAVER_MAPS_CLIENT_ID

    if (!clientId) {
      console.error('[NaverMapLoader] Client ID가 설정되지 않음')
      reject(new Error('네이버 맵 API Client ID가 설정되지 않았습니다.'))
      isScriptLoading = false
      return
    }

    // 이미 스크립트가 로드되어 있는지 확인
    if (typeof window !== 'undefined' && (window as any).naver?.maps) {
      console.log('[NaverMapLoader] naver.maps 이미 존재, resolve')
      isScriptLoaded = true
      isScriptLoading = false
      resolve()
      return
    }

    const scriptUrl = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${clientId}`
    console.log('[NaverMapLoader] 스크립트 로드 시작', {
      url: scriptUrl,
      currentUrl: typeof window !== 'undefined' ? window.location.href : '',
    })

    const script = document.createElement('script')
    script.id = NAVER_MAP_SCRIPT_ID
    script.type = 'text/javascript'
    script.src = scriptUrl
    script.async = true

    script.onload = () => {
      console.log('[NaverMapLoader] script onload fired')
      const waitForMaps = (retries = 20) => {
        const hasMaps = typeof (window as any).naver?.maps !== 'undefined'
        const hasMapConstructor =
          typeof (window as any).naver?.maps?.Map !== 'undefined'
        if (hasMaps) {
          ensureScriptLoaded(
            NAVER_MARKER_CLUSTER_SCRIPT_ID,
            NAVER_MARKER_CLUSTER_SCRIPT_URL
          )
            .then(() => {
              const hasMarkerClustering =
                typeof (window as any).MarkerClustering !== 'undefined'
              if (!hasMarkerClustering) {
                throw new Error('MarkerClustering 초기화에 실패했습니다.')
              }

              console.log('[NaverMapLoader] naver.maps 준비됨', {
                hasMapConstructor,
                hasMarkerClustering,
                retriesLeft: retries,
              })
              isScriptLoaded = true
              isScriptLoading = false
              resolve()
            })
            .catch((error) => {
              console.error('[NaverMapLoader] MarkerClustering 로드 실패', error)
              isScriptLoading = false
              loadPromise = null
              reject(error)
            })
          return
        }
        if (retries <= 0) {
          console.error('[NaverMapLoader] 초기화 시간 초과', {
            hasNaver: typeof (window as any).naver,
            hasMaps: typeof (window as any).naver?.maps,
          })
          isScriptLoading = false
          loadPromise = null
          reject(new Error('네이버 맵 API 초기화 시간 초과'))
          return
        }
        if (retries % 5 === 0) {
          console.log('[NaverMapLoader] naver.maps 대기 중...', {
            retriesLeft: retries,
            hasNaver: !!((window as any).naver),
          })
        }
        setTimeout(() => waitForMaps(retries - 1), 100)
      }
      waitForMaps()
    }

    script.onerror = (error) => {
      console.error('[NaverMapLoader] 스크립트 로드 실패:', error)
      isScriptLoading = false
      loadPromise = null
      reject(new Error('네이버 맵 API 스크립트 로드 실패'))
    }

    // 인증 실패 시 확인할 수 있는 전역 함수 설정
    // 이 함수는 스크립트가 로드된 후에 호출될 수 있으므로, 이미 로드된 경우에는 reject하지 않음
    const originalAuthFailure = (window as any).navermap_authFailure
    ;(window as any).navermap_authFailure = () => {
      console.error('[NaverMapLoader] 네이버 맵 API 인증 실패')
      console.error('[NaverMapLoader] 현재 URL:', window.location.href)
      console.error('[NaverMapLoader] Client ID:', clientId)
      
      // 이미 로드된 경우에는 reject하지 않음 (지도는 표시되지만 일부 기능 제한)
      if (!isScriptLoaded) {
        isScriptLoading = false
        loadPromise = null
        reject(new Error('네이버 맵 API 인증 실패: 클라이언트 아이디와 웹 서비스 URL을 확인해주세요. 네이버 개발자 콘솔에 현재 URL을 등록해야 합니다.'))
      }
      
      // 기존 핸들러가 있으면 호출
      if (originalAuthFailure && typeof originalAuthFailure === 'function') {
        originalAuthFailure()
      }
    }

    document.head.appendChild(script)
    console.log('[NaverMapLoader] script appendChild 완료')
  })

  return loadPromise
}

/**
 * 네이버 맵 API가 로드되었는지 확인
 */
export const isNaverMapLoaded = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    typeof (window as any).naver !== 'undefined' &&
    typeof (window as any).naver.maps !== 'undefined'
  )
}
